import { useHistory, useLocation } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { getAddRoute, getIndexRoute } from "../../../main/Routing";
import { useEntityCount } from "../../../api/BaseEntityApi";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { DataSource, DataSourceFilters, DataSourceFiltersTranslator } from "../../../api/DataSource";
import { DataSourceFilterBar, DataSourceFilterForm } from "./DataSourceFilterBar";
import { BridgeFieldLabels, BridgeFilters, bridgeConstants } from "../../../api/Bridges";
import { Alert } from "../../../common/overlays/Alert/Alert";
import { useAutoloadClientList } from "../../../api/autoload/AutoloadClientsApi";
import { useMultiEdit } from "../../../common/forms/MultiEditForms/common/MultiEditUtils";
import { NotAvailable, NotSet } from "../../../common/misc/UIconstants";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { CircularCountdown } from "../../../common/loaders/CircularCountdown/CircularCountdown";
import { GetPersons } from "../../../common/misc/EntityRenders/EntityRenderer";
import { useQueryClient } from "@tanstack/react-query";
import { BridgeConnectionStatus } from "../../Bridges/common/BridgeConnectionStatus";
import { DataSourceRunningStatus } from "../common/DataSourceRunningStatus/DataSourceRunningStatus";
import { ToggleIndicator } from "../../../common/badges/ToggleIndicator/ToggleIndicator";
import { ParameterFormatter } from "../../../ParameterViewer/TreeViewer/ParameterFormatter";
import { dataFormatsConstants } from "../../../api/DataFormats";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { customImportConstants } from "../../../api/CustomImports";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { EntityTableCloneButton } from "../../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableDeleteButton } from "../../../common/entity/entityComponents/EntityTableDeleteButton";
import { EntityTableEditButton } from "../../../common/entity/entityComponents/EntityTableEditButton";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";
import { useListen } from "../../../common/signalr/SignalRContext";
import { NestedDropdown } from "../../../common/buttons/NestedDropdown/NestedDropdown";
import { ExportCsvButton } from "../../../common/tables/ExportCsvButton/ExportCsvButton";

const parameterFormatter = new ParameterFormatter();

export const switchDataSourcesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<DataSourceFilters>>[] | null | undefined
): SortState<InferSortByOptions<DataSourceFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "TYPE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-type",
          property: sort.property,
        };

      case "BRIDGE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-bridge",
          property: sort.property,
        };

      case "BRIDGE_TYPE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-bridgeType",
          property: sort.property,
        };

      case "ENABLED":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-enabled",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "ASC",
          headerId: "default-name",
          property: "NAME",
        };
    }
  });
};

export const defaultDataSourceFilters: DataSourceFilters = {
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
};

export const useDataSourceTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"dataSources">) => {
  const defaults: ColumnsSettings<DataSource> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: "Data Source-ID", property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-enabled": { pos: 2, active: true, header: fieldLabels.enabled, property: "enabled" },
      "default-bridge": { pos: 5, active: true, header: fieldLabels.bridge, property: "bridge" },
      "default-bridgeStatus": { pos: 6, active: false, header: "Connection status" },
      "default-bridgeType": { pos: 7, active: true, header: BridgeFieldLabels.type, property: "bridge.type" },
      "default-nextScheduled": {
        pos: 8,
        active: false,
        header: fieldLabels.nextScheduledDate,
        property: "status.nextScheduledDate",
      },
      "default-interval": {
        pos: 9,
        active: true,
        header: fieldLabels.intervalInSeconds,
        property: "intervalInSeconds",
      },
      "default-status": { pos: 10, active: false, header: "Status", property: "status" },
      "default-startedOn": {
        pos: 11,
        active: false,
        header: "Last started on",
        property: "status.lastClientStatus.startedOn",
      },
      "default-duration": {
        pos: 12,
        active: false,
        header: "Duration of last run",
        property: "status.lastClientStatus.duration",
      },
      "default-format": { pos: 13, active: true, header: fieldLabels.format, property: "format" },
      "default-directories": {
        pos: 14,
        active: true,
        header: fieldLabels.directories,
        property: "directories",
      },
      "default-excludedFiles": {
        pos: 15,
        active: false,
        header: fieldLabels.fileExcludePatterns,
        property: "fileExcludePatterns",
      },
      "default-customImport": {
        pos: 16,
        active: false,
        header: fieldLabels.customImport,
        property: "customImport",
      },
      "default-type": { pos: 17, active: false, header: fieldLabels.type, property: "type" },
      "default-enteredOn": { pos: 18, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 19, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 20, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 21, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [
      fieldLabels.bridge,
      fieldLabels.enteredBy,
      fieldLabels.enteredOn,
      fieldLabels.customImport,
      fieldLabels.directories,
      fieldLabels.enabled,
      fieldLabels.fileExcludePatterns,
      fieldLabels.format,
      fieldLabels.intervalInSeconds,
      fieldLabels.modifiedBy,
      fieldLabels.modifiedOn,
      fieldLabels.name,
      fieldLabels.nextScheduledDate,
      fieldLabels.type,
    ]
  );

  const tabStoreDefaults: TableTabsDict<DataSource, DataSourceFilters, DataSourceFilterForm> = useMemo(() => {
    let def: TableTabsDict<DataSource, DataSourceFilters, DataSourceFilterForm> = {
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultDataSourceFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      status: {
        tabId: "status",
        type: "fixed",
        label: "Status",
        title: "Status",
        icon: "play",
        align: "left",
        xPos: 1,
        settings: {
          columnSettings: {
            "default-id": { pos: 0, active: false, header: "Data Source-ID", property: "id" },
            "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
            "default-enabled": { pos: 4, active: false, header: fieldLabels.enabled, property: "enabled" },
            "default-bridge": { pos: 5, active: false, header: fieldLabels.bridge, property: "bridge" },
            "default-bridgeStatus": { pos: 6, active: true, header: "Connection status" },
            "default-bridgeType": { pos: 7, active: false, header: BridgeFieldLabels.type, property: "bridge.type" },
            "default-nextScheduled": {
              pos: 8,
              active: true,
              header: fieldLabels.nextScheduledDate,
              property: "status.nextScheduledDate",
            },
            "default-interval": {
              pos: 9,
              active: true,
              header: fieldLabels.intervalInSeconds,
              property: "intervalInSeconds",
            },
            "default-status": { pos: 10, active: true, header: "Status", property: "status" },
            "default-startedOn": {
              pos: 11,
              active: true,
              header: "Last started on",
              property: "status.lastClientStatus.startedOn",
            },
            "default-duration": {
              pos: 12,
              active: true,
              header: "Duration of last run",
              property: "status.lastClientStatus.duration",
            },
            "default-format": { pos: 13, active: false, header: fieldLabels.format, property: "format" },
            "default-directories": {
              pos: 14,
              active: false,
              header: fieldLabels.directories,
              property: "directories",
            },
            "default-excludedFiles": {
              pos: 15,
              active: false,
              header: fieldLabels.fileExcludePatterns,
              property: "fileExcludePatterns",
            },
            "default-customImport": {
              pos: 16,
              active: false,
              header: fieldLabels.customImport,
              property: "customImport",
            },
            "default-type": { pos: 17, active: false, header: fieldLabels.type, property: "type" },
            "default-enteredOn": {
              pos: 18,
              active: false,
              header: fieldLabels.enteredOn,
              property: "enteredOn",
            },
            "default-enteredBy": {
              pos: 19,
              active: false,
              header: fieldLabels.enteredBy,
              property: "enteredBy",
            },
            "default-modifiedOn": {
              pos: 20,
              active: false,
              header: fieldLabels.modifiedOn,
              property: "modifiedOn",
            },
            "default-modifiedBy": {
              pos: 21,
              active: false,
              header: fieldLabels.modifiedBy,
              property: "modifiedBy",
            },
          },
          columnWidths: {},
          filters: defaultDataSourceFilters,
          sidebarFilters: { enabled: { id: true, name: "Enabled" } },
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { enabled: true },
          sidebarFilters: { enabled: { id: true, name: "Enabled" } },
        },
      },
    };
    return def;
  }, [
    fieldLabels.bridge,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.customImport,
    fieldLabels.directories,
    fieldLabels.enabled,
    fieldLabels.fileExcludePatterns,
    fieldLabels.format,
    fieldLabels.intervalInSeconds,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    fieldLabels.name,
    fieldLabels.nextScheduledDate,
    fieldLabels.type,
  ]);
  return { defaults, tabStoreDefaults };
};
export const useDataSourcesTableColumns = ({
  fieldLabels,
  entityConstants,
  sort,
  setSort,
}: UseEntityTableProps<"dataSources">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<DataSource> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => (
          <span style={{ color: "var(--gray-400)" }}>
            <samp>{row.id}</samp>
          </span>
        ),
        width: 150,
        minWidth: 100,
        align: "right",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        id: "default-name",
        Header: fieldLabels.name,
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
          </div>
        ),
        width: 200,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        id: "default-bridge",
        Header: fieldLabels.bridge,
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={bridgeConstants.icon} color={"var(--primary)"} /> {row.bridge.name}
              </span>
            </div>
          </div>
        ),
        width: 150,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("BRIDGE", sort, setSort),
      },
      {
        id: "default-bridgeType",
        Header: BridgeFieldLabels.type,
        accessor: (row) => (
          <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            <label className="label label-soft-info" style={{ margin: 0 }}>
              <LucideIcon name="wifi" /> {row.bridge.type}
            </label>
          </div>
        ),
        width: 140,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("BRIDGE_TYPE", sort, setSort),
      },
      {
        id: "default-bridgeStatus",
        Header: "Connection status",
        accessor: (row) => (
          <BridgeConnectionStatus bridge={row.bridge} dataSource={row} hideCheckConnection disableLiveUpdates />
        ),
        width: 180,
        minWidth: 100,
        align: "left",
      },
      {
        id: "default-status",
        Header: "Status",
        accessor: (row) => <DataSourceRunningStatus dataSource={row} />,
        width: 150,
        minWidth: 100,
        align: "left",
      },
      {
        id: "default-enabled",
        Header: fieldLabels.enabled,
        accessor: (row) => <ToggleIndicator enabled={row.enabled} />,
        minWidth: 100,
        width: 100,
        align: "left",
        ...getAsyncSortingFn("ENABLED", sort, setSort),
      },
      {
        id: "default-interval",
        Header: fieldLabels.intervalInSeconds,
        accessor: (row) => parameterFormatter.formatDuration(row.intervalInSeconds),
        minWidth: 100,
        width: 150,
        align: "left",
        ...getAsyncSortingFn("INTERVAL", sort, setSort),
      },
      {
        id: "default-type",
        Header: fieldLabels.type,
        accessor: (row) => (
          <label className="label label-soft-secondary" style={{ margin: 0 }}>
            {row.type}
          </label>
        ),
        width: 150,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("TYPE", sort, setSort),
      },
      {
        id: "default-startedOn",
        Header: "Current run started on",
        accessor: (row) => (
          <>
            {row.status?.lastClientStatus?.startedOn && row.status?.lastClientStatus?.runState === "Running" ? (
              <DateTimeRenderer date={row.status.lastClientStatus.startedOn} includeElapsed={true} />
            ) : (
              NotAvailable
            )}
          </>
        ),
        minWidth: 100,
        width: 240,
        align: "left",
      },

      {
        id: "default-duration",
        Header: "Last run duration",
        accessor: (row) => (
          <>
            {row.status?.lastClientStatus?.duration && row.status?.lastClientStatus?.runState !== "Running"
              ? parameterFormatter.formatDuration(row.status.lastClientStatus.duration, 1)
              : NotAvailable}
          </>
        ),
        minWidth: 100,
        width: 160,
        align: "left",
      },
      {
        id: "default-nextScheduled",
        Header: "Next scheduled",
        accessor: (row) => (
          <>
            {row.enabled &&
            row.status?.lastClientStatus?.runState !== "Running" &&
            row.status?.lastClientStatus?.startedOn &&
            row.status.nextScheduledDate ? (
              <div className="flex row-nowrap align-center gap-5">
                <CircularCountdown
                  startTime={new Date(row.status.lastClientStatus.startedOn)}
                  endTime={new Date(row.status.nextScheduledDate)}
                  width={24}
                  style={{ stroke: "var(--primary)" }}
                />
                <DateTimeRenderer date={row.status?.nextScheduledDate} includeElapsed={true} />
              </div>
            ) : (
              NotAvailable
            )}
          </>
        ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        id: "default-format",
        Header: fieldLabels.format,
        accessor: (row) =>
          row.format?.name ? (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 600,
                whiteSpace: "nowrap",
                marginRight: "5px",
              }}
            >
              <LucideIcon name={dataFormatsConstants.icon} color="var(--primary)" />
              <span> {row.format.name}</span>
            </div>
          ) : (
            NotSet
          ),
        width: 150,
        minWidth: 100,
        align: "left",
      },
      {
        id: "default-customImport",
        Header: fieldLabels.customImport,
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={customImportConstants.icon} color={"var(--primary)"} />{" "}
                {row.customImport?.name ?? row.customImport?.id ?? NotSet}
              </span>
            </div>
          </div>
        ),
        width: 140,
        minWidth: 100,
        align: "left",
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
      {
        id: "default-directories",
        Header: fieldLabels.directories,
        accessor: (row) => (
          <div style={{ overflow: "auto", display: "flex", flexWrap: "wrap", gap: "5px" }}>
            {row.directories?.map((f, i) => (
              <span
                key={f + i}
                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                className="label label-soft-default"
                title={`File path: ${f}`}
              >
                {f}
              </span>
            ))}
          </div>
        ),
        width: 300,
        minWidth: 100,
        align: "left",
      },
      {
        id: "default-excludedFiles",
        Header: fieldLabels.fileExcludePatterns,
        accessor: (row) => (
          <div style={{ overflow: "auto", display: "flex", flexWrap: "wrap", gap: "5px" }}>
            {row.fileExcludePatterns?.map((f) => (
              <span
                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                className="label label-soft-default"
                title={`Regular expression: ${f.regex}`}
              >
                {f.name}
              </span>
            ))}
          </div>
        ),
        width: 200,
        minWidth: 100,
        align: "left",
      },
    ];

    return headers;
  }, [
    fieldLabels.id,
    fieldLabels.name,
    fieldLabels.bridge,
    fieldLabels.enabled,
    fieldLabels.intervalInSeconds,
    fieldLabels.type,
    fieldLabels.format,
    fieldLabels.customImport,
    fieldLabels.enteredOn,
    fieldLabels.enteredBy,
    fieldLabels.modifiedOn,
    fieldLabels.modifiedBy,
    fieldLabels.directories,
    fieldLabels.fileExcludePatterns,
    sort,
    setSort,
    entityConstants.icon,
  ]);
  return { columns };
};
export const DataSourcesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"dataSources">) => {
  const history = useHistory();
  const { route, session } = useContext(SessionContext);
  const [showModal, setShowModal] = useState(false);
  const { bulkDeleteMutationAsync, isLoadingBulkDeleteMutation } = entityApi;

  const { applyPayload } = useMultiEdit<DataSource>({ resource: entityConstants.resource });

  const { defaults, tabStoreDefaults } = useDataSourceTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<DataSource>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<DataSource, DataSourceFilters, DataSourceFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchDataSourcesDefaultSortState,
      dispatchTabStore,
    });

  const { data: clients, refetch: refetchClients } = useAutoloadClientList();
  const queryClient = useQueryClient();

  const [refetchTimeout, setRefetchTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
  const refetchDataSources = useCallback(() => {
    if (refetchTimeout) clearTimeout(refetchTimeout);
    setRefetchTimeout(
      setTimeout(() => {
        functionRef.current?.refetch();
      }, 500)
    );
    return () => refetchTimeout && clearTimeout(refetchTimeout);
  }, [functionRef, refetchTimeout]);

  const refetchBridges = useCallback(
    (bridgeId: number) => {
      queryClient.invalidateQueries({ queryKey: [bridgeConstants.resource, "detail", bridgeId] });
    },
    [queryClient]
  );

  useListen("ClientConnectionChanged", refetchBridges);
  useListen("ClientListUpdated", refetchClients);
  useListen("DataSourceStatusChanged", refetchDataSources);

  const { columns } = useDataSourcesTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  // additional informations
  const { data: bridgeCount } = useEntityCount<BridgeFilters>(bridgeConstants.resource, {
    includeCount: true,
  });

  // Client approval
  const clientsWaitingForApproval = useMemo(() => clients?.filter((client) => !client.isApproved), [clients]);

  // TODO: remove once generic in tab store
  const { search: queryString } = useLocation();
  useEffect(() => {
    if (!tabsLoading) {
      let params = Object.fromEntries(new URLSearchParams(queryString));
      if ("tabId" in params && !!params["tabId"]) (async () => await onTabChange(params["tabId"]))();
    }
  }, [history, onTabChange, queryString, tabsLoading]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <button
            className="btn btn-primary"
            onClick={() => {
              history.push(route(getAddRoute(bridgeConstants.frontendIndexRoute)));
            }}
          >
            <LucideIcon name="plus" /> Add {bridgeConstants.entitySingular}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => history.push(route(getAddRoute(entityConstants.frontendIndexRoute)))}
            disabled={!bridgeCount?.count}
          >
            <LucideIcon name="plus" /> Add {entityConstants.entitySingular}
          </button>

          <button
            className="btn btn-default"
            onClick={() => {
              // setShowBridgesModal(true);
              history.push(route(getIndexRoute(bridgeConstants.frontendIndexRoute)));
            }}
            disabled={!bridgeCount?.count}
          >
            <LucideIcon name="table" /> List {bridgeConstants.entityPlural}
          </button>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <DataSourceFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <div className="flex col-nowrap gap-5" style={{ width: "100%" }}>
                {Array.isArray(clientsWaitingForApproval) && clientsWaitingForApproval.length > 0 && (
                  <>
                    <Alert
                      type="warning"
                      message={
                        <div className="flex row-nowrap gap-5 align-center">
                          There are clients waiting to be configured.
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() =>
                              history.push(`${route(getAddRoute(bridgeConstants.frontendIndexRoute))}?step=2`)
                            }
                          >
                            Setup clients
                          </button>
                        </div>
                      }
                      fit
                      style={{ marginBottom: 0 }}
                    />
                  </>
                )}
                <div className="flex row-nowrap gap-5">
                  <EntityFilterIndicator<DataSource, DataSourceFilters>
                    filters={filters}
                    excludeFilters={{
                      enabled: (value) => currentTab !== "status" && value !== undefined && value !== null,
                    }}
                    translatorConsts={DataSourceFiltersTranslator}
                  />
                  <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
                  {selection.size > 0 && (
                    <div style={{ display: "flex", width: "fit-content", height: "100%", alignItems: "center" }}>
                      <span
                        style={{
                          color: "var(--gray-400)",
                          fontWeight: "normal",
                          padding: "0 5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selection.size}{" "}
                        {selection.size === 1 ? entityConstants.entitySingular : entityConstants.entityPlural} selected
                      </span>
                    </div>
                  )}
                  <EntityTableEditButton
                    entityConstants={entityConstants}
                    routes={routes}
                    permissions={permissions}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                  />
                  <MoreDropdown drop="right" btn="btn btn-ghost-secondary" disabled={selection.size === 0}>
                    <li>
                      <button
                        className="btn btn-ghost-success btn-sm"
                        disabled={selection.size === 0 || !session?.permissions.administer_autoload}
                        title={`Enable the selected ${entityConstants.entityPlural}`}
                        onClick={async () => {
                          await applyPayload(Array.from(selection), { enabled: true }).catch(() => {});
                        }}
                      >
                        <LucideIcon name="toggle-right" /> Enable
                      </button>
                    </li>
                    <li>
                      <button
                        className="btn btn-ghost-warning btn-sm"
                        disabled={selection.size === 0 || !session?.permissions.administer_autoload}
                        title={`Disable the selected ${entityConstants.entityPlural}`}
                        onClick={async () => {
                          await applyPayload(Array.from(selection), { enabled: false }).catch(() => {});
                        }}
                      >
                        <LucideIcon name="toggle-left" /> Disable
                      </button>
                    </li>
                    <li>
                      <EntityTableCloneButton
                        entityConstants={entityConstants}
                        routes={routes}
                        permissions={permissions}
                        selection={selection}
                      />
                    </li>
                    <li>
                      <EntityTableDeleteButton
                        entityConstants={entityConstants}
                        permissions={permissions}
                        selection={selection}
                        selectionPermissions={selectionPermissions}
                        onClick={() => setShowModal(true)}
                      />
                    </li>
                    <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                      <li>
                        <ExportCsvButton
                          entityConstants={entityConstants}
                          columnSettings={columnSetting}
                          filters={{
                            ids: selection.size > 0 ? Array.from(selection) : undefined,
                            ...memoizedFilters,
                            searchTerm: debouncedSearchValue,
                          }}
                          disabled={selection.size === 0}
                        />
                      </li>
                    </NestedDropdown>
                  </MoreDropdown>
                  <AlertModal
                    type="danger"
                    showModal={showModal}
                    setShowModal={setShowModal}
                    title={`${
                      selection.size === 0
                        ? `Select ${entityConstants.entitySingular} to delete`
                        : selection.size === 1
                          ? `Delete the selected ${entityConstants.entitySingular}`
                          : `Delete the selected ${entityConstants.entityPlural}`
                    }`}
                    description={`Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`}
                    proceedLabel="Delete"
                    onProceed={async () => {
                      await bulkDeleteMutationAsync({
                        ids: Array.from(selection),
                        goBackOnSuccess: false,
                        showToast: false,
                        entityName: entityConstants.entityPlural,
                        params: { deletePermanently: true },
                      }).catch((e) => {});
                      functionRef.current?.resetSelection();
                      setShowModal(false);
                    }}
                    loading={isLoadingBulkDeleteMutation}
                  />
                </div>
              </div>
            </EntityTable.Controls>
            <EntityTable.Body<DataSource, DataSourceFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
              // useInfiniteQueryOptions={{ cacheTime: 300000, staleTime: 300000 }}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
